import { api } from '../config';
import { HistoricalDataParams, HistoricalDataResponse, StatisticalDataParams, StatisticalDataResponse, AggregatedDataParams, AggregatedDataResponse } from './types';

/**
 * Fetch historical data from the timescaledb endpoint
 */
export async function getHistoricalData(params: HistoricalDataParams): Promise<HistoricalDataResponse> {
  try {
    // Add default timezone if not provided
    const requestParams = {
      ...params,
      tz: params.tz || 'Asia/Bangkok'
    };

    console.log('Fetching historical data with params:', requestParams);

    const response = await api.post(
      '/historical_data/timescaledb/aggregated_data/query/',
      requestParams
    );

    console.log('Raw API response (already extracted by interceptor):', response);
    console.log('Response type:', typeof response);

    // The response interceptor already extracts response.data, so 'response' here is actually response.data
    // Check if the response is already the data array (old format)
    if (Array.isArray(response)) {
      console.log('Response is an array (old format), converting to new format');
      return {
        success: true,
        status: 200,
        message: "Successfully retrieved aggregated data",
        metadata: {
          timestamp: new Date().toISOString(),
          version: "1.0"
        },
        data: {
          timezone: requestParams.tz,
          data: response
        }
      };
    }

    // Check if response.data is an array (old format)
    if (response && response.data && Array.isArray(response.data)) {
      console.log('Response has old data structure, converting to new format');
      return {
        success: response.success || true,
        status: response.status || 200,
        message: response.message || "Successfully retrieved aggregated data",
        metadata: response.metadata || {
          timestamp: new Date().toISOString(),
          version: "1.0"
        },
        data: {
          timezone: requestParams.tz,
          data: response.data
        }
      };
    }

    // If we get here, assume it's already in the new format
    console.log('Response structure:', {
      success: response?.success,
      status: response?.status,
      hasData: !!response?.data,
      dataType: typeof response?.data
    });

    return response;
  } catch (error) {
    console.error('Error fetching historical data:', error);
    throw error;
  }
}

/**
 * Fetch aggregated data from the timescaledb endpoint
 * Used for daily aggregated data queries with site_id support
 */
export async function getAggregatedData(params: AggregatedDataParams): Promise<AggregatedDataResponse> {
  try {
    // Add default timezone if not provided
    const requestParams = {
      ...params,
      tz: params.tz || 'Asia/Bangkok'
    };

    const response = await api.post(
      '/historical_data/timescaledb/aggregated_data/query/',
      requestParams
    );

    // The response interceptor already extracts response.data, so 'response' here is actually response.data
    // No need for backward compatibility handling as this is a new function
    return response;
  } catch (error) {
    console.error('Error fetching aggregated data:', error);
    throw error;
  }
}

/**
 * Fetch statistical data from the timescaledb endpoint for energy analysis
 */
export async function getStatisticalData(params: StatisticalDataParams): Promise<StatisticalDataResponse> {
  try {
    // Add default timezone if not provided
    const requestParams = {
      ...params,
      tz: params.tz || 'Asia/Bangkok'
    };

    console.log('Fetching statistical data with params:', requestParams);

    const response = await api.post(
      '/historical_data/timescaledb/statistical_data/query/',
      requestParams
    );

    console.log('Raw statistical API response:', response);

    return response.data;
  } catch (error) {
    console.error('Error fetching statistical data:', error);
    throw error;
  }
}
